import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { confirmDialog } from '../ConfirmDialog';
import {
  deleteOutlookEmail,
  getOutlookFolders,
  getOutlookLiveEmails,
  moveOutlookEmail,
  updateOutlookEmail,
} from '../../services/api';

// Inbox (Focused) and Sent Items come from the stored list with AI fields
// (EmailsProvider); every other folder, Other and search are read live.
export const STORED_FOLDERS = { inbox: 'Inbox', sentitems: 'Sent Items' };

export const isLiveView = ({ folder, focused, search }) =>
  Boolean(search) ||
  !((folder === 'inbox' && focused === 'focused') || folder === 'sentitems');

export default function useOutlookMailbox({ token, enabled, refreshKey }) {
  const [view, setView] = useState({
    folder: 'inbox',
    focused: 'focused',
    search: '',
  });
  const [folders, setFolders] = useState([]);
  const [live, setLive] = useState({ emails: [], hasMore: false, page: 1 });
  const [liveLoading, setLiveLoading] = useState(false);
  const isLive = isLiveView(view);

  useEffect(() => {
    if (!enabled) return;
    getOutlookFolders(token)
      .then((data) => setFolders(data?.folders || []))
      .catch(() => setFolders([]));
  }, [token, enabled, refreshKey]);

  const loadLive = useCallback(async () => {
    setLiveLoading(true);
    try {
      const data = await getOutlookLiveEmails(
        {
          folder: view.folder,
          focused: view.folder === 'inbox' ? view.focused : undefined,
          search: view.search || undefined,
          page: live.page,
        },
        token,
      );
      setLive((prev) => ({
        ...prev,
        emails: data?.emails || [],
        hasMore: Boolean(data?.has_more),
      }));
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Could not load mail.');
    } finally {
      setLiveLoading(false);
    }
  }, [token, view, live.page]);

  useEffect(() => {
    if (enabled && isLive) loadLive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isLive, view, live.page, refreshKey]);

  const changeView = (next) => {
    setView((prev) => ({ ...prev, ...next }));
    setLive({ emails: [], hasMore: false, page: 1 });
  };

  const setPage = (page) => setLive((prev) => ({ ...prev, page }));

  // Swaps the changed mail into the live list, or drops it when it left the view.
  const replaceLive = (id, mail) =>
    setLive((prev) => ({
      ...prev,
      emails: mail
        ? prev.emails.map((e) => (e.id === id ? { ...e, ...mail } : e))
        : prev.emails.filter((e) => e.id !== id),
    }));

  const refreshFolders = () =>
    getOutlookFolders(token)
      .then((data) => setFolders(data?.folders || []))
      .catch(() => {});

  // Returns the updated mail, or null when it moved out of this view.
  const act = async (email, action, value) => {
    const id = email.message_id || email.id;
    try {
      let result;
      if (action === 'read') {
        result = await updateOutlookEmail(id, { isRead: value }, token);
      } else if (action === 'flag') {
        result = await updateOutlookEmail(id, { flagged: value }, token);
      } else if (action === 'delete') {
        // Both cases ask first and say where the mail goes.
        const deletedItems = folders.find((f) => f.name === 'Deleted Items');
        const inDeletedItems =
          deletedItems && email.parentFolderId === deletedItems.id;
        const ok = await confirmDialog(
          inDeletedItems
            ? {
                title: 'Delete from Deleted Items?',
                message:
                  'It moves to Recoverable Items in Outlook, where it can still be restored for about 14 days.',
                confirmLabel: 'Delete',
                danger: true,
              }
            : {
                title: 'Delete this email?',
                message:
                  'It moves to Deleted Items in Outlook. You can restore it from there.',
                confirmLabel: 'Delete',
                danger: true,
              },
        );
        if (!ok) return undefined;
        if (inDeletedItems) {
          await deleteOutlookEmail(id, token);
          toast.success('Moved to Recoverable Items');
        } else {
          await moveOutlookEmail(id, 'deleteditems', token);
          toast.success('Moved to Deleted Items');
        }
        result = null;
      } else {
        await moveOutlookEmail(id, value, token);
        result = null;
        toast.success(value === 'archive' ? 'Archived' : 'Moved');
      }
      replaceLive(id, result);
      refreshFolders();
      return result;
    } catch (error) {
      toast.error(
        typeof error === 'string' ? error : 'Outlook did not accept that.',
      );
      return undefined;
    }
  };

  return {
    view,
    changeView,
    folders,
    isLive,
    liveEmails: live.emails,
    liveHasMore: live.hasMore,
    livePage: live.page,
    setLivePage: setPage,
    liveLoading,
    act,
  };
}
